import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { Typography } from '@euler/app/components/typography';
import { ConstructionReportJobProductList } from '@euler/app/flows/report/components/construction/ConstructionReportJobProductList';
import {
  ConstructionCellModel,
  useConstructionReportModels,
} from '@euler/app/flows/report/functions/useConstructionReportModels';
import { ProductManifestProps } from '@euler/app/flows/report/types';
import { Center } from '@euler/components/Center';
import { Colors } from '@euler/components/Colors';
import { Collapsible } from '@euler/components/collapsible';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useContainerDimensions } from '@euler/functions/useAdaptiveContainerWidth';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { VehicleReport } from '@euler/model/report';
import { memo, useMemo } from 'react';
import { FlatList, ScrollViewProps, Text, View } from 'react-native';
import { useTheme } from 'styled-components';
import { JobDetailItemView } from './JobDetailItemView';
import { getConstructionReportItemStatusInfo } from './util';

export const ConstructionReportView = memo(
  ({
    report,
    productManifest,
    targetJobId,
    faultLikeSubject,
    ...scrollViewProps
  }: {
    report: VehicleReport;
    productManifest?: ProductManifestProps;
    targetJobId?: number;
    faultLikeSubject?: FaultInspectionItem;
  } & ScrollViewProps) => {
    const { cells } = useConstructionReportModels(report, {
      targetJobId,
      faultLikeSubject,
    });
    return (
      <FlatList
        {...scrollViewProps}
        data={cells}
        keyExtractor={x => String(x.index)}
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        renderItem={({ item }) => (
          <Cell
            model={item}
            productManifest={productManifest}
            showNumber={targetJobId == null}
          />
        )}
      />
    );
  },
);

const Header = memo(
  ({
    model,
    showNumber = true,
  }: {
    model: ConstructionCellModel;
    showNumber?: boolean;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const statusInfo = getConstructionReportItemStatusInfo(model.status);
    return (
      <>
        <HStack
          css={`
            flex: 1;
            padding-right: ${rpx(4)}px;
          `}
        >
          <Text
            css={`
              color: ${theme.report.construction.section.header.titleColor};
              left: ${rpx(-3)}px;
            `}
          >
            {showNumber && (
              <Text
                css={`
                  font-weight: bold;
                  font-style: italic;
                  font-size: ${rpx(28)}px;
                `}
              >
                {model.index + 1}.{' '}
              </Text>
            )}
            <Text
              css={`
                font-family: ${FontFamily.NotoSans.Medium};
                font-size: ${rpx(19)}px;
              `}
            >
              {model.name}
            </Text>
          </Text>
          <Center
            css={`
              border-width: 1px;
              border-color: ${statusInfo.color};
              margin-left: ${rpx(8)}px;
              padding: 0 ${rpx(10)}px;
              height: ${rpx(22)}px;
              border-radius: ${rpx(11)}px;
            `}
          >
            <Typography.Label
              color={statusInfo.color}
              regular
              size={rpx(12)}
              lineHeight={rpx(20)}
              css={`
                text-align: center;
              `}
            >
              {statusInfo.text}
            </Typography.Label>
          </Center>
        </HStack>
      </>
    );
  },
);

const kSectionContentSpacing = 15;

const Content = memo(
  ({
    model,
    productManifest,
  }: {
    model: ConstructionCellModel;
    productManifest?: ProductManifestProps;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const { width: containerWidth } = useContainerDimensions();

    const contentWidth = containerWidth - rpx(kSectionContentSpacing) * 2;
    const mediaHeight = contentWidth * 0.75;

    return (
      <>
        <View
          css={`
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            margin-top: ${rpx(10)}px;
          `}
        >
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Light};
              font-size: ${rpx(15)}px;
              color: ${Colors.Gray2};
            `}
          >
            检测技师: {model.technicianName}
          </Text>
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Light};
              font-size: ${rpx(13)}px;
              color: ${Colors.Gray2};
              text-align: right;
            `}
          >
            {model.count}项
          </Text>
        </View>
        {model.details.length > 0 && (
          <View
            css={`
              margin-top: ${rpx(10)}px;
            `}
          >
            {model.details.map((detail, i) => (
              <JobDetailItemView
                key={i}
                title={detail.title}
                status={model.status}
                defectiveLevel={detail.defectiveLevel}
              />
            ))}
          </View>
        )}
        {model.remark != null && model.remark.trim().length > 0 && (
          <HStack
            justifyContent="flex-start"
            css={`
              margin-top: ${rpx(10)}px;
            `}
          >
            <Center
              css={`
                background-color: ${theme.colors.status.success};
                margin-right: ${rpx(4)}px;
                padding: 0 ${rpx(8)}px;
                border-radius: ${rpx(9)}px;
                height: ${rpx(18)}px;
              `}
            >
              <Text
                css={`
                  font-family: ${FontFamily.NotoSans.Light};
                  font-size: ${rpx(12)}px;
                  text-align: center;
                  color: #fff;
                  line-height: ${rpx(16)}px;
                `}
              >
                备注
              </Text>
            </Center>
            <Text
              css={`
                flex: 1;
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(14)}px;
                margin-right: ${rpx(4)}px;
                line-height: ${rpx(18)}px;
                color: ${Colors.Gray3};
              `}
            >
              {model.remark.trim()}
            </Text>
          </HStack>
        )}
        <ConstructionReportJobProductList
          jobId={model.jobId}
          jobName={model.name}
          productManifest={productManifest}
          productConfirmations={model.productConfirmations}
          css={`
            padding-top: ${rpx(10)}px;
            margin-top: ${rpx(10)}px;
          `}
        />
        {model.procedures.map((procedure, i) => (
          <View
            key={i}
            css={`
              padding-top: ${rpx(10)}px;
              margin-top: ${rpx(10)}px;
            `}
          >
            <Text
              css={`
                font-family: ${FontFamily.NotoSans.Medium};
                font-size: ${rpx(16)}px;
                color: ${theme.report.construction.procedure.titleColor};
                line-height: ${rpx(20)}px;
                margin-bottom: ${rpx(5)}px;
              `}
            >
              {procedure.name}
            </Text>
            <MediaCarousel
              medias={procedure.medias}
              noPlaceholder
              css={`
                width: ${contentWidth}px;
                height: ${mediaHeight}px;
                border-radius: ${rpx(8)}px;
                overflow: hidden;
              `}
            />
          </View>
        ))}
      </>
    );
  },
);

const Cell = memo(
  ({
    model,
    showNumber,
    productManifest,
  }: {
    model: ConstructionCellModel;
    productManifest?: ProductManifestProps;
    showNumber?: boolean;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const header = useMemo(
      () => <Header model={model} showNumber={showNumber} />,
      [model, showNumber],
    );

    return (
      <Collapsible.Panel
        header={header}
        caretColor={theme.report.construction.section.header.titleColor}
        headerStyle={{ alignItems: 'flex-start' }}
        css={`
          margin-top: ${rpx(10)}px;
          background-color: ${theme.section.background};
        `}
      >
        <Content model={model} productManifest={productManifest} />
      </Collapsible.Panel>
    );
  },
);
